
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Stack,
    useTheme,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const PdfMerger = () => {
    const theme = useTheme();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const processFiles = (newFiles) => {
        setError('');
        setSuccess('');

        const validFiles = Array.from(newFiles).filter(file => {
            // Accept PDFs and Images for merging
            return file.type === 'application/pdf' || file.type.startsWith('image/');
        });

        if (validFiles.length < newFiles.length) {
            setError('Some files were skipped. Only PDF and Image files are supported.');
        }

        setFiles(prev => [...prev, ...validFiles]);
    };

    const handleFileChange = (event) => {
        if (event.target.files) processFiles(event.target.files);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files) processFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleMerge = async () => {
        if (files.length < 2) {
            setError('Please select at least 2 files to merge.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await fetch('/api/merge-pdfs', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to merge PDFs');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'merged_document.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            setSuccess('Files merged successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: theme.palette.text.primary }}>
                PDF Merger
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Combine multiple PDF or Image files into a single PDF document.
            </Typography>

            <Paper
                elevation={0}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                    p: 4,
                    border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: 4,
                    textAlign: 'center',
                    bgcolor: dragActive ? (theme.palette.mode === 'dark' ? 'rgba(0, 242, 255, 0.05)' : 'rgba(0, 120, 255, 0.05)') : theme.palette.background.default,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: 150,
                    mb: 4
                }}
                onClick={() => document.getElementById('merge-upload').click()}
            >
                <input
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    id="merge-upload"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                />

                <Stack alignItems="center" spacing={2}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary', opacity: dragActive ? 1 : 0.5 }} />
                    <Typography variant="h6">
                        {dragActive ? "Drop Files here" : "Click or Drag Files here"}
                    </Typography>
                </Stack>
            </Paper>

            {files.length > 0 && (
                <Paper sx={{ mb: 3 }}>
                    <List>
                        {files.map((file, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <IconButton edge="end" onClick={() => removeFile(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{success}</Alert>}

            <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <MergeTypeIcon />}
                onClick={handleMerge}
                disabled={loading || files.length < 2}
            >
                {loading ? 'Merging...' : 'Merge Files'}
            </Button>
        </Box>
    );
};

export default PdfMerger;
